'use strict'

import { workspace } from '@constants/URL'
import con from '@services/connection'
import { GlobalBus } from '@services/bus'
import store from '@state/store'
import { WEBSOCKET, RELOAD, FEATURES } from '@constants/Text'

// WebSocket.CONNECTING	0
// WebSocket.OPEN	1
// WebSocket.CLOSING	2
// WebSocket.CLOSED	3

/**
 * 400 - token invalido
 * 502 -
 */
export default class WSWorkspace {
  constructor() {
    this.token = ''
    this.URL_WORKSPACE = ''
    this.$socket = {}
    this.timeoutPing = null
    this.TIME_SPACE = 60000
    this.loading = false

    this.timeoutCallAgain = null

    if (WebSocket) {
      // A feature WORKSPACE só é conhecida depois do login (managePermissions
      // popula parameters/show). No load do módulo ela ainda é false, então
      // este connect() inicial é no-op para todos; a conexão real dispara pelo
      // watcher abaixo assim que a flag vira true. Escritórios sem a feature
      // nunca conectam — evita 1 unhandledrejection/aba (getToken 401).
      // .catch defensivo: connect() é async; um throw síncrono em _openSocket
      // (ex.: `new WebSocket` lançando) não pode virar unhandledrejection —
      // seria justamente a classe de erro que este arquivo elimina.
      this.connect().catch(() => {})
      this._watchWorkspaceFeature()
    } else {
      this._reportUnsupportedWebSocket()
    }
  }

  _reportUnsupportedWebSocket() {
    const aux = {}
    if (navigator) {
      aux.name = navigator.appName
      aux.version = navigator.appVersion
      aux.agent = navigator.userAgent
      aux.platform = navigator.platform
      aux.cookie = navigator.cookieEnabled
      aux.codeName = navigator.appCodeName
    }
    this.sendError(
      'Workspace',
      'socket:constructor',
      'Browser não suporta WebScoket',
      JSON.stringify(aux)
    )
  }

  // Lê a feature WORKSPACE de forma defensiva — o getter não existe antes do
  // login (e vem vazio em testes). Ausência = desabilitado.
  _isWorkspaceEnabled() {
    try {
      const canShow = store.getters['parameters/canShow']
      return typeof canShow === 'function' && Boolean(canShow(FEATURES.WORKSPACE))
    } catch (_error) {
      return false
    }
  }

  // Dispara a conexão assim que a flag WORKSPACE vira true (após o login/
  // managePermissions). Só reage à transição para true; escritórios sem a
  // feature nunca disparam.
  _watchWorkspaceFeature() {
    try {
      store.watch(
        () => this._isWorkspaceEnabled(),
        (enabled) => {
          // connect() já revalida a flag e tem guard de `loading`, então
          // chamar direto é seguro (sem risco de conexões duplicadas).
          // .catch defensivo: connect() é async — não deixar um throw síncrono
          // em _openSocket escapar como unhandledrejection.
          if (enabled) {
            this.connect().catch(() => {})
          }
        }
      )
    } catch (_error) {
      // store.watch indisponível (ex.: store mockado em teste) — ignore.
    }
  }

  async connect() {
    // Gate por feature flag: escritório sem WORKSPACE não abre socket nem pede
    // token. Precede o guard de `loading` para não agendar retries inúteis.
    if (!this._isWorkspaceEnabled()) {
      return
    }
    if (this.loading) {
      clearTimeout(this.timeoutCallAgain)
      this.timeoutCallAgain = setTimeout(() => {
        this.connect()
      }, 3000)
      return
    }
    this.loading = true
    try {
      await this.getToken()
    } catch (tokenError) {
      // Sem token não há socket — o workspace/chat fica desabilitado para este
      // usuário. `loading` permanece true de propósito: preserva o dead-end
      // pré-existente (sem retry storm). Esta rejeição não-tratada era a fonte
      // de 1 CLIENT_ERROR com client_message vazio por aba no Loki.
      console.warn('[WS] Token do workspace indisponível', tokenError)
      return
    }

    if (this.token) {
      this._openSocket()
    } else {
      console.warn('[WS] Token indisponível')
    }

    setTimeout(() => {
      this.loading = false
    }, 10000)
  }

  // Fecha o socket anterior (listeners + handlers inline) e abre um novo com o
  // token corrente. Extraído de connect() para mantê-lo enxuto.
  _openSocket() {
    if (this.$socket && this._onOpen) {
      this.$socket.removeEventListener('open', this._onOpen)
      this.$socket.removeEventListener('message', this._onMessage)
    }

    // Neutralize inline handlers to prevent reconnect during cleanup
    if (this.$socket && this.$socket.readyState !== undefined) {
      this.$socket.onclose = null
      this.$socket.onerror = null
      if (
        this.$socket.readyState === WebSocket.CONNECTING ||
        this.$socket.readyState === WebSocket.OPEN
      ) {
        this.$socket.close()
      }
    }

    const socket = new WebSocket(
      this.URL_WORKSPACE + `?X-Token-API=${this.token}`
    )
    this.config(socket)
    this.$socket = socket
    this.ping()
    store.dispatch('websocket/setWorkspaceConnected', this.isConnected())
  }

  config(socket) {
    this._onOpen = (e) => {
      console.warn('[WS] ON OPEN', e)
      store.dispatch('websocket/setWorkspaceConnected', this.isConnected())
    }
    socket.addEventListener('open', this._onOpen)

    this._onMessage = (d) => {
      store.dispatch('websocket/setWorkspaceConnected', this.isConnected())
      console.warn('[WS] On message', d)
      let msg = JSON.parse(d.data)
      if (msg.status === 200) {
        msg = msg.value
        if (msg.evento_tipo === 'message') {
          GlobalBus.$emit(WEBSOCKET.WORKSPACE_MESSAGE, msg)
          GlobalBus.$emit(WEBSOCKET.WORKSPACE_RELOAD_MSG, true)
          GlobalBus.$emit(RELOAD.ROOMS, { show_loading: false })
        }

        if (msg.evento_tipo === 'room_created') {
          GlobalBus.$emit(RELOAD.ROOMS)
        }

        if (msg.evento_tipo === 'room_deleted') {
          GlobalBus.$emit(RELOAD.ROOMS)
        }
      }
    }
    socket.addEventListener('message', this._onMessage)

    // Comentário temporário para verificar se para de fazer requisições demais
    socket.onclose = (e) => {
      this.sendError(
        'Workspace',
        'socket:onclose',
        'Gatilho acionado de close no workspace.',
        JSON.stringify({
          type: e.type,
          target: { url: e.target.url },
          code: e.code,
        })
      )
      console.warn('[WS] ON CLOSE', e)
      // this.ping();
      this.connect()
      store.dispatch('websocket/setWorkspaceConnected', this.isConnected())
    }
    socket.onerror = (e) => {
      this.sendError(
        'Workspace',
        'socket:onerror',
        'Gatilho acionado de erro no workspace.',
        JSON.stringify({ type: e.type, target: { url: e.target.url } })
      )
      console.warn('[WS] ON ERROR', e)
      // this.ping();
      this.connect()
      store.dispatch('websocket/setWorkspaceConnected', this.isConnected())
    }

    console.warn('[WS] configurado')
  }

  sendMessage(message, try_again) {
    if (this.isConnected()) {
      let aux = {
        action: 'send_message',
        mensagem: '',
        token_sala: '',
        'X-Token-API': this.token,
      }

      console.warn('[WS] Enviando mensagem')

      const msg = Object.assign({}, aux, message)

      try {
        this.$socket.send(JSON.stringify(msg))
      } catch (e) {
        console.error(e)
        if (try_again) {
          this.sendMessage(message)
        }
      }
    }

    if (this.$socket.readyState === this.$socket.CONNECTING) {
      setTimeout(() => {
        this.sendMessage(message, true)
      }, 300)
    }

    if (
      [this.$socket.CLOSED, this.$socket.CLOSING].includes(
        this.$socket.readyState
      )
    ) {
      this.connect()
      setTimeout(() => {
        this.sendMessage(message, true)
      }, 300)
    }
  }

  disconnect() {
    clearTimeout(this.timeoutPing)
    clearTimeout(this.timeoutCallAgain)
    // this.$socket começa como {} (constructor) e só vira um WebSocket real
    // após _openSocket(). Um logout antes disso (feature desabilitada, token
    // ainda em voo, getToken falhou) chega aqui com {} — truthy mas sem
    // close(), o que lançava "this.$socket.close is not a function"
    // (AAWZ-WEB-3Y0). Guard por typeof evita a chamada nesse cenário.
    if (this.$socket && typeof this.$socket.close === 'function') {
      if (this._onOpen) {
        this.$socket.removeEventListener('open', this._onOpen)
      }
      if (this._onMessage) {
        this.$socket.removeEventListener('message', this._onMessage)
      }
      this.$socket.onclose = null
      this.$socket.onerror = null
      this.$socket.close()
    }
    this.token = ''
  }

  /**
   * retorno: {"status": 200, "message": "Conexao Atualizada com sucesso", "value": {"connectionId": "Olb7Me-giYcCG3Q="}}
   */
  ping() {
    clearTimeout(this.timeoutPing)
    this.timeoutPing = setTimeout(() => {
      console.warn('[WS] Ping')
      try {
        this.$socket.send(
          JSON.stringify({
            action: 'ping',
            message: '',
            token_sala: '',
            'X-Token-API': this.token,
          })
        )
      } catch (e) {
        console.error('[WS] Erro ping', e)
        this.connect()
        store.dispatch('chat/loadRooms', false)
      }
    }, this.TIME_SPACE)
  }

  isConnected() {
    return this.$socket.readyState === this.$socket.OPEN
  }

  async getToken() {
    const r = await con.get(workspace.token)
    if (r && r.websocket) {
      this.URL_WORKSPACE = r.websocket.url + r.websocket.stage
    }
    if (r && r.status === 200 && r.value) {
      this.token = r.value.token
      return this.token
    }
    this.token = ''
    // Rejeitar com Error (nunca string vazia) — o reason chega inteiro na
    // telemetria caso algum caller esqueça o catch (client_message + stack).
    throw new Error(
      `Token do workspace indisponível (status ${(r && r.status) || 'desconhecido'})`
    )
  }

  /**
   * Enviar notificacao de erro para backend.
   * @param {String} source Origem do erro
   * @param {String} reason Motivo do erro
   * @param {String} message Mensagem do erro
   * @param {JSON} additional_info Informacoes adicionais
   */
  sendError(source, reason, message, additional_info) {
    const data = {
      source: source,
      reason: reason,
      message: message,
      additional_info: additional_info,
    }
    con.post(workspace.report_erro, data)
  }
}
